import type { ShippingQuoteSnapshot } from './services/shipping';
import type { VatLine } from './services/vat';
import type { BonusTotals, CommissionResult } from './services/bonus';

export enum UserRole {
  ADMIN = 'ADMIN',
  TEACHER = 'TEACHER',
  STUDENT = 'STUDENT',
  GUEST = 'GUEST'
}

export type UserStatus = 'PENDING' | 'ACTIVE' | 'REJECTED' | 'EXPIRED' | 'DEACTIVATED';

// --- COMPANY INFO TYPES ---
export interface CompanySettings {
    name: string;
    orgNumber: string;
    email: string;
    phone: string;
    street: string;
    zip: string;
    city: string;
    logoUrl?: string;
    bankGiro?: string;
    swish?: string;
    /** Momsregistreringsnummer, t.ex. SE556677889901 — krävs på fakturor. */
    vatNumber?: string;
    /** Innehar F-skattsedel. Styr texten "Godkänd för F-skatt" på fakturor. */
    fTax?: boolean;
}

// --- CMS / SITE CONFIG TYPES ---

export type StyleMode = 'modern' | 'classic' | 'soft' | 'bold';

export type ProductCardStyle = 'classic' | 'overlay' | 'minimal' | 'brutal' | 'float';
export type ProductPageLayout = 'split' | 'immersive' | 'editorial' | 'grid' | 'drawer';

// New Type for Hero Layouts
export type HeroLayoutType = 'card' | 'split' | 'fade' | 'banner' | 'editorial' | 'video';

export interface SiteTheme {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  logoUrl?: string;      
  logoWhiteUrl?: string; 
  iconUrl?: string;      
  logoHeight?: number;   
  heroOverlayOpacity?: number; 
  footerText?: string;   
  styleMode?: StyleMode; 
  
  glassDensity?: number; 
  glassTintColor?: string; 
  componentRadius?: number; 
  scaleMode?: 'sm' | 'md' | 'lg'; 
  
  productCardStyle?: ProductCardStyle;
  productPageLayout?: ProductPageLayout;

  // Typography overrides for Hero
  heroTitleFont?: string;
  heroTextFont?: string;

  // Product Card Specifics
  cardBorderColor?: string;
  cardBackgroundColor?: string;
  cardTitleColor?: string;
  cardPriceColor?: string;
  cardButtonColor?: string;
  cardButtonTextColor?: string;
}

export type SectionType = 'HERO' | 'FEATURES' | 'CONTENT' | 'CONTACT' | 'PRODUCTS' | 'PRODUCT_GRID' | 'REVIEWS' | 'MEDIA';

export interface PageSection {
  id: string;
  type: SectionType;
  title?: string;
  text?: string;
  imageUrl?: string;
  mobileImageUrl?: string; 
  buttons?: { 
      label: string; 
      action: string; 
      primary: boolean;
      backgroundColor?: string; // New: Override per button
      textColor?: string;       // New: Override per button
  }[];
  features?: { icon: string; title: string; text: string }[];
  layout?: 'image-left' | 'image-right' | 'center';

  // --- BÄSTSÄLJARE (PRODUCTS) ---
  /** Produkterna som visas, i ordning, när admin väljer själv. */
  productIds?: string[];
  /** 'manual' = admins val (standard), 'bestselling' = mest sålda senaste 30 dagarna. */
  productSource?: 'manual' | 'bestselling';
  /** Hur många produkter som visas, 3–6. */
  productLimit?: number;

  // --- OMDÖMEN (REVIEWS) OCH BILDRULLE (MEDIA) ---
  /** Bara omdömen som är markerade som utvalda i Butikens innehåll. */
  featuredOnly?: boolean;
  
  // --- HERO DESIGN PROPS ---
  heroLayout?: HeroLayoutType; 
  heroVideoUrl?: string; // NEW: Specific for Video Hero
  
  heroTitleColor?: string; 
  heroTextColor?: string; 
  heroTitleFont?: string; 
  heroTextFont?: string; 
  
  // Effects
  heroShadow?: 'none' | 'soft' | 'hard' | 'strong' | 'glow'; 
  heroTitleShadow?: 'none' | 'soft' | 'hard' | 'strong' | 'glow'; 
  heroTextShadow?: 'none' | 'soft' | 'hard' | 'strong'; 
  heroTitleTexture?: 'none' | 'gold' | 'coffee' | 'silver' | 'gradient';
  
  // Text Outline (Stroke)
  heroTitleOutlineWidth?: 'none' | 'sm' | 'md' | 'lg';
  heroTitleOutlineColor?: string;
  heroTextOutlineWidth?: 'none' | 'sm' | 'md';
  heroTextOutlineColor?: string;

  heroSubtitleSize?: 'sm' | 'md' | 'lg' | 'xl';
  heroTitleSize?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'; 
  heroHeight?: 'small' | 'medium' | 'large' | 'full'; 

  // New positioning props
  heroTextAlignment?: 'left' | 'center' | 'right';
  heroContentPosition?: number; // 0 to 100 percentage (Horizontal X)
  heroContentPositionY?: number; // 0 to 100 percentage (Vertical Y)
  
  // Button styling (Global for section)
  heroButtonColor?: string;
  heroButtonTextColor?: string;
}

export interface FooterLink {
  label: string;
  url: string;
}

export interface FooterColumn {
  id: string;
  title: string;
  links: FooterLink[];
}

export interface FooterConfig {
  tagline?: string;
  contact: {
    email?: string;
    phone?: string;
    address?: string;
  };
  socials: {
    instagram?: string;
    facebook?: string;
    tiktok?: string;
    linkedin?: string;
    youtube?: string;
  };
  columns: FooterColumn[];
}

export interface SiteConfig {
  id: string; 
  theme: SiteTheme;
  sections: PageSection[];
  footer?: FooterConfig;
}

// --- INFO PAGES CONFIG ---
export interface InfoPageData {
    title: string;
    subtitle: string;
    content: string; // HTML/Rich text
}

export interface InfoPagesConfig {
    about: InfoPageData;
    coffee: InfoPageData;
    terms: InfoPageData;
    faq: InfoPageData;
    privacy: InfoPageData; // New GDPR Privacy Page
}

// --- EXISTING TYPES ---

/** Vikt och mått för frakt. Tomt = okänt (på en variant: produktens värde gäller). */
export interface PackageSpec {
  weightGrams?: number;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
}

/** En produkt som ingår i ett paket. */
export interface BundleItem {
  productId: string;
  /** Bestämd variant av delen, t.ex. malet. */
  variantId?: string;
  quantity: number;
}

export interface ProductVariant extends PackageSpec {
  id: string;
  name: string; 
  price: number;
  attributes: Record<string, string>; 
  imageUrl?: string; 
  images?: string[]; 
  stock?: number;
  classProfit?: number;
  purchasePrice?: number; // Renamed from companyProfit
  /** Paket: variantens eget innehåll, t.ex. hela bönor eller malet. */
  bundleItems?: BundleItem[];
}

export interface Product extends PackageSpec {
  id: string;
  name: string;
  description: string;
  price: number; 
  imageUrl: string; 
  images: string[]; 
  classProfit?: number;
  purchasePrice?: number; // Renamed from companyProfit
  variants?: ProductVariant[];
  /** Länken i produktetikettens QR-kod. Tom = butiken (coffeenova.se/shop). */
  labelUrl?: string;
  /** Produktkategorin, som bestämmer momsen. Utan kategori visas produkten inte i butiken. */
  categoryId?: string;
  /** Fast provisionssats för gruppen (t.ex. 0.5 = 50 %). Tomt = bonustrappan 35/40/45 %. */
  fixedCommissionRate?: number;
  /** Paket: produkterna som ingår. Momsen, vikten och inköpet räknas från dem. */
  bundleItems?: BundleItem[];
  /** Grupperna som ser produkten i butiken. Tomt = alla grupper. */
  visibleClassIds?: string[];
  /** Raden under namnet i butiken, t.ex. "Malet · 250 g". Paket: tomt = innehållet. */
  subtitle?: string;
  /** Säsongstagg på kortet och produktsidan, t.ex. "Limited Edition". */
  seasonTag?: string;
  /** Lagerstatus som admin väljer (inte kopplad till lagersaldo). */
  stockStatus?: StockStatus;
  /** 3–5 punkter på produktsidan. */
  highlights?: string[];
  /** "Du kanske också gillar", valda i admin (högst 4). */
  relatedProductIds?: string[];
}

/** En riktig recension som admin lagt in. Utan produkt visas den bara i recensionsraden. */
export interface ShopReview {
  id: string;
  name: string;
  /** 1–5 stjärnor. */
  rating: number;
  text: string;
  productId?: string;
  published: boolean;
  /** Kundens ansiktsbild (bara riktiga ansikten, med kundens tillstånd). Visas i de runda bilderna. */
  avatarUrl?: string;
  /** Bilder och videor som kunden delat med recensionen. */
  media?: { url: string; kind: 'image' | 'video' }[];
  /** Utvald i admin: citatet under priset på produktsidan. Annars den nyaste med 5 stjärnor. */
  featured?: boolean;
  /** När recensionen lades in (ISO). Nyast först. */
  createdAt?: string;
}

/** Kundbild eller kundvideo (UGC) utan recension. Bildtexten (t.ex. namnet) visas under. */
export interface ShopMedia {
  id: string;
  url: string;
  kind: 'image' | 'video';
  caption?: string;
}

/** "Kaffet" och "Om oss" på produktsidan: stor bild med texten i en färgad panel. */
export interface ShopStory {
  title: string;
  text: string;
  imageUrl?: string;
  /** Snabbfakta som etiketter överst i kortet, t.ex. "100 % arabica". Högst 4. */
  facts?: string[];
}

/** Produktsidans innehåll (site_content 'shop_content'). */
export interface ShopContent {
  reviews: ShopReview[];
  media: ShopMedia[];
  coffee: ShopStory;
  about: ShopStory;
}

/** in_stock = i lager, low = få kvar, very_low = nästan slut, out = slut i lager (kan inte köpas). */
export type StockStatus = 'in_stock' | 'low' | 'very_low' | 'out';

export interface ClassGroup {
  id: string;
  name: string;
  teacherId: string;
  salesGoal: number;
  deliveryAddress?: string;
  campaignStart?: number;
  campaignEnd?: number;
  deletedAt?: number;
  // Renewal Fields
  renewalStatus?: 'NONE' | 'REQUESTED' | 'PROCESSING';
  requestedName?: string;
  /** Föreningens bankgiro eller konto, för avräkningar där CoffeeNova betalar ut. */
  payoutAccount?: string;
  /** Betalsätten admin slagit på för gruppen. */
  paymentOptions?: PaymentOptions;
}

/** Vilka betalsätt en grupp erbjuder i kassan (admin väljer per grupp). */
export interface PaymentOptions {
  /** Betala i kassan (kort, Swish, Klarna via Stripe) till CoffeeNova. */
  stripe: boolean;
  /** Hemleverans, betalas alltid i kassan. Kräver stripe. */
  homeDelivery: boolean;
  /** Swish till säljaren; säljarens nummer visas för kunden. */
  sellerSwish: boolean;
  /** Betala direkt till säljaren. */
  sellerDirect: boolean;
  /** Endast hemleverans: betalning i kassan bara med hemleverans; hämtning betalas till säljaren. */
  stripeHomeOnly: boolean;
}

export const DEFAULT_PAYMENT_OPTIONS: PaymentOptions = { stripe: false, homeDelivery: false, sellerSwish: false, sellerDirect: true, stripeHomeOnly: false };

/** SELLER = direkt till säljaren, SWISH = Swish till säljaren, STRIPE = i kassan till CoffeeNova. */
export type PaymentMethod = 'SELLER' | 'SWISH' | 'STRIPE';

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  validUntil: number; 
  classId?: string;
  assignedClassId?: string;
  uniqueSlug?: string;
  avatarUrl?: string;
  deletedAt?: number;
  /** Säljarens Swishnummer (tio siffror), för grupper med Swish till säljaren. */
  swishNumber?: string;
  /** Ansvarigs svar på "Vilken grupp tillhör du?" vid registrering, som admin matchar mot rätt grupp. */
  requestedGroup?: string;
  /** När kontot skapades (ms). */
  createdAt?: number;
}

export interface OrderItem {
  productId: string;
  productNameSnapshot?: string; // Frozen name
  variantId?: string;
  variantName?: string; // Frozen name
  quantity: number;
  priceAtMoment: number;
  /** Paket: kundens val per del när ordern skickas. Ordern sparar valen i components. */
  choices?: Record<string, string>;
  appliedBoost?: number; // How much extra profit was added per item
  /** Momssatsen när ordern lades (från produktens kategori). */
  vatRate?: number;
  /** Fast provisionssats (t.ex. 0.5) sparad när ordern lades. Saknas = bonustrappan. */
  fixedCommissionRate?: number | null;
  campaignId?: string; // Which campaign triggered it
  
  // Financial Snapshot
  purchasePriceSnapshot?: number; // NEW: Cost at time of purchase
  classProfitSnapshot?: number; // Often same as product.classProfit but good to freeze
  
  // Legacy support (Do not use for new orders)
  companyProfit?: number; 
  /** Paket: delarna med sin andel av priset, sparade när ordern lades. */
  components?: OrderItemComponent[];
}

/** En del i en paketrad. allocated är delens andel av ett pakets pris (alla st av delen). */
export interface OrderItemComponent {
  productId: string;
  variantId?: string;
  name: string;
  variantName?: string;
  quantity: number;
  /** Delens ordinarie styckpris. */
  unitPrice: number;
  allocated: number;
  vatRate: number;
  purchasePrice?: number;
  weightGrams?: number;
}

export type DeliveryMethod = 'PICKUP' | 'HOME';

export interface Order {
  id: string;
  classId?: string; 
  invoiceId?: string; // NEW: Linked Invoice
  studentId: string;
  studentName?: string; 
  classNameSnapshot?: string; 
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerAddress: string;
  totalAmount: number;
  status: 'PENDING' | 'SUBMITTED' | 'CONFIRMED' | 'DELIVERED';
  /** AWAITING_PAYMENT: hemleverans som väntar på Stripe (syns bara för admin). REFUNDED: återbetald. */
  paymentStatus: 'PAID' | 'UNPAID' | 'AWAITING_PAYMENT' | 'REFUNDED';
  /** PICKUP = hämtas hos säljaren och betalas till säljaren. HOME = hemleverans, betald i kassan. */
  deliveryMethod?: DeliveryMethod;
  paymentMethod?: PaymentMethod;
  /** Frakt inkl. moms. Ingår inte i totalAmount och ger ingen provision. */
  shippingAmount?: number;
  shippingWeightGrams?: number;
  shippingPostalCode?: string;
  shippingCity?: string;
  /** Fraktberäkningen databasen sparade när ordern lades (PostNord-kostnad, förpackning, vikt). */
  shippingQuote?: ShippingQuoteSnapshot | null;
  /** Vägt paket (admin, efter packning). */
  shippingWeighedGrams?: number;
  shippingLengthCm?: number;
  shippingWidthCm?: number;
  shippingHeightCm?: number;
  paidAt?: number;
  shippedAt?: number;
  trackingNumber?: string;
  refundedAt?: number;
  /** Moms per sats, räknad av databasen när ordern lades (frakten inräknad). */
  vatBreakdown?: VatLine[];
  timestamp: number;
  items: OrderItem[];
  deletedAt?: number; 
  marketingConsent?: boolean;
  /** Varifrån kunden kom, om besöket hade en UTM-kod (QR på säljkort, reklamblad …). */
  utm?: { source?: string; medium?: string; campaign?: string; content?: string } | null;
}

export interface Invoice {
    id: string;
    invoiceNumber: string;
    classId: string;
    recipientSnapshot: {
        name: string;
        email: string;
        address?: string;
        /** Föreningens konto, för avräkningar där CoffeeNova betalar ut. */
        payoutAccount?: string;
    };
    senderSnapshot?: CompanySettings; // NEW: Snapshot of company details
    linesSnapshot: {
        description: string;
        /** Momssats för raden. Saknas på äldre fakturor; null = ingen moms (provision). */
        vatRate?: number | null;
        /** Enhetspris exklusive moms (krav på fullständig faktura). Saknas på äldre fakturor. */
        unitPriceExVat?: number;
        quantity: number;
        unitPrice: number;
        total: number;
        /** Specifikation under raden, t.ex. vilka kassaköp provisionen gäller. Påverkar inga belopp. */
        details?: string[];
        /** Varurad, rabatt enligt bonustrappan eller provision för kassaköp (styr klassens version). */
        kind?: 'product' | 'discount' | 'provision';
    }[];
    financialSnapshot: {
        totalSales: number;
        /** Saknas på fakturor från före hemleveransen (då var allt hämtat hos säljarna). */
        collectedBySellers?: number;
        paidOnline?: number;
        classProfit: number;
        /** Negativt = avräkning, CoffeeNova betalar ut till gruppen. */
        toPay: number;
        /** Moms per sats på det gruppen betalar. Saknas på fakturor från före momskategorierna (då 12 %). */
        vatBreakdown?: VatLine[];
        /** Gruppens provision på köp betalda i kassan, utan moms. */
        onlineProvision?: number;
        /** Leveransdag till gruppen, "2026-10-15". Avgör momssatsen. */
        deliveryDate?: string;
        /** Bonustrappan för fakturan. Saknas på fakturor från före bonussystemet. */
        bonus?: {
            priorRegularSales: number;
            regularSales: number;
            regularCommission: number;
            fixedSales: number;
            fixedCommission: number;
            /** Nivån efter fakturan, t.ex. 0.4. */
            levelRate: number;
        };
        vatAmount: number;
        netAmount: number;
    };
    status: 'DRAFT' | 'SENT' | 'PAID';
    createdAt: number;
    dueDate: number;
    sentAt?: number;
    /** När fakturan markerades betald. Saknas på fakturor från före betaldatumet. */
    paidAt?: number;
}

export interface CartItem {
  product: Product;
  variant?: ProductVariant;
  quantity: number;
  /** Paket: kundens val per del, t.ex. { [Flaskans id]: färgens variant-id }. */
  choices?: Record<string, string>;
}

export interface StudentStats {
  id: string;
  name: string;
  /** Kronor per säljare. Bara för ansvarig och admin; säljare får aldrig andras (eller sina egna) summor. */
  totalSales?: number;
  orderCount: number;
  /** Antal sålda produkter. Topplistan rankar på detta. */
  itemsSold: number;
  isArchived?: boolean;
}

/** Statistik per grupp för översikterna. */
export interface ClassStats {
  classInfo: ClassGroup;
  students: StudentStats[];
  /** Gruppens försäljning i kronor (alla produkter). */
  totalSales: number;
  /** Bonusläget i perioden: vanliga produkter (trappan) och fasta satser. */
  bonus: BonusTotals;
  /** Aktiva säljare, för "sälj ca X var". */
  sellerCount: number;
}

export type RecipientType = 'ALL' | 'ROLE' | 'INDIVIDUAL' | 'GROUP' | 'CUSTOMERS';

export interface Newsletter {
  id: string;
  subject: string;
  content: string;
  imageUrl?: string;
  sentAt: number;
  expiresAt?: number; 
  recipientType: RecipientType;
  recipientTargetId?: string; 
  recipientGroupId?: string; 
  recipients?: string[]; 
  senderId: string;
}

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  content: string;
  createdAt: number;
  isRead: boolean;
}

export interface Conversation {
  id: string;
  teacherId: string;
  adminId: string | null;
  teacherName?: string;
  adminName?: string;
  subject: string;
  updatedAt: number;
  unreadCount?: number;
}

// --- CAMPAIGNS ---

export interface ProfitCampaign {
  id: string;
  name: string;
  boostAmount: number;
  startDate: number;
  endDate: number;
  isActive: boolean;
  isVisibleInStore: boolean; 
  targetProductIds?: string[]; 
  targetClassIds?: string[]; 
}

// --- ORDER LOGIC TYPES ---

export type BundleStatus = 'PENDING' | 'SUBMITTED' | 'CONFIRMED' | 'DELIVERED' | 'MIXED';

export interface OrderBundle {
    classId: string;
    className: string;
    teacherName: string;
    deliveryAddress: string;
    orderCount: number;
    totalAmount: number;
    status: BundleStatus;
    orders: Order[];
    latestDate: number;
    // New
    invoiceId?: string;
    /** Vanliga produkter som redan fakturerats i gruppens period (bonustrappan fortsätter därifrån). */
    priorInvoicedRegularSales?: number;
}

export interface FinancialSummary {
    totalSales: number;
    /** Ordrar som hämtats: pengarna finns hos gruppen. */
    collectedBySellers: number;
    /** Hemleveranser betalda i kassan: pengarna finns redan hos CoffeeNova. */
    paidOnline: number;
    /** Gruppens provision enligt bonustrappan (plus fasta satser). */
    totalClassProfit: number;
    totalCompanyProfit: number;
    /** Hämtat hos säljarna − gruppens vinst. Negativt = CoffeeNova betalar ut till gruppen. */
    toPay: number;
    /** Bonusens uppställning för just dessa ordrar. */
    bonus: CommissionResult;
    /** Vanliga produkter som redan fakturerats i perioden innan dessa ordrar. */
    priorRegularSales: number;
}
